import { Asset, Entry } from "contentful";
import * as React from "react";
// @ts-ignore
import TextTruncate from "react-text-truncate";
import {
  // useTranslation,
  withTranslation,
  WithTranslation,
} from "react-i18next";
import { MultiButton } from "../../components/Buttons";
import LocaleNavigationLink from "../../components/LocaleNavigationLink";
import NavigationLink from "../../components/NavigationLink";
import {
  ICategory,
  IArticle,
  IBlogCategory,
  IInternalNavigationLink,
  IExternalNavigationLink,
  IQuestionCategory,
} from "../../contentfulTypes";
import { getCachedQuery } from "../../util/contentfulQueries";
import PictureWrapper from "../../components/PictureWrapper";
import loadingSpinner from "../../Assets/Loading/SVG/loading.svg";

export type SubMenuType =
  | "Activity categories"
  | "Accommodation categories"
  | "Question categories"
  | "Itineraries"
  | "Custom links"
  | "Blogs";

interface IProps extends WithTranslation {
  type: SubMenuType;
  baseLink: string;
  baseImage?: Asset;
  links?: Array<Entry<IInternalNavigationLink | IExternalNavigationLink>>;
  active?: boolean;
  onClick?: (event: any) => void;
}

interface IState {
  data: Array<Entry<ICategory | IQuestionCategory>>;
  linksData: Array<Entry<IInternalNavigationLink | IExternalNavigationLink>>;
  blogData: Array<{
    category: Entry<IBlogCategory>;
    articles: Array<Entry<IArticle>>;
  }>;
  activeCategoryId: string | null;
  isLoading: boolean;
}

const types = new Map<SubMenuType, CategorySystemName>([
  ["Activity categories", "category"],
  ["Accommodation categories", "category"],
  ["Itineraries", "itinerary"],
  ["Question categories", "questionCategory"],
  ["Blogs", "article"],
]);

const codeToLang = {
  en: "English",
  nl: "Dutch",
  fr: "French",
  de: "German",
  pt: "Portuguese",
  es: "Spanish",
};

// TODO: move to separate file
export type CategorySystemName =
  | "category"
  | "itinerary"
  | "questionCategory"
  | "article";

const TOP_POSTS_ID = "top-posts";

// TODO: move to separate util class
export const getCategoryUrl = (linkType: CategorySystemName, slug: string) => {
  if (linkType === "category") {
    return `/category/${slug}`;
  }
  if (linkType === "questionCategory") {
    return `/questions/${slug}`;
  }
  if (linkType === "itinerary") {
    return `/itinerary/${slug}`;
  }
  return slug;
};

export const getCategorySort = (linkType: CategorySystemName) => {
  if (linkType === "itinerary") {
    return (a: any, b: any) =>
      a.fields.itineraryDays.length - b.fields.itineraryDays.length;
    // removed :
    /*
      a.fields.itineraryDays &&
      a.fields.itineraryDays.length - b.fields.itineraryDays &&
      b.fields.itineraryDays.length;
	*/
  }
  return undefined;
};

class NavigationSubMenu extends React.Component<IProps, IState> {
  private isFetching = false;
  private loaded = false;
  private isPrerender = /prerender|bot|crawler|spider|headless/.test(
    navigator.userAgent.toLowerCase()
  );

  constructor(props: IProps) {
    super(props);
    this.state = {
      data: [],
      linksData: [],
      blogData: [],
      activeCategoryId: null,
      isLoading: false,
    };
    if (props.type !== "Blogs" || this.isPrerender) {
      this.getData(props.i18n.language);
    }
  }

  // Needed, because after language switch this component stays mounted and
  // data needs to be refetched in the correct language.
  public componentDidUpdate(prevProps: IProps) {
    if (this.props.type === "Blogs") {
      const blogLocaleStale =
        this.state.blogData.length > 0 &&
        this.state.blogData[0].category.sys.locale !== this.props.i18n.language;

      if (blogLocaleStale) {
        this.loaded = false;
        this.isFetching = false;
        this.setState({ blogData: [], activeCategoryId: null });
      }
      if (this.props.active && !this.loaded) {
        this.loaded = true;
        this.getData(this.props.i18n.language);
      }
      return;
    }

    const blogLocaleStale2 =
      this.state.blogData.length > 0 &&
      this.state.blogData[0].category.sys.locale !== this.props.i18n.language;

    if (
      !this.state.data ||
      (this.state.data.length > 0 &&
        this.state.data[0].sys.locale !== this.props.i18n.language) ||
      !this.state.linksData ||
      (this.state.linksData.length > 0 &&
        this.state.linksData[0].sys.locale !== this.props.i18n.language) ||
      blogLocaleStale2
    ) {
      this.getData(this.props.i18n.language);
    }
  }

  public getData = async (language: string) => {
    if (this.isFetching) {
      return;
    }
    this.isFetching = true;

    try {
      if (this.props.type === "Blogs") {
        this.setState({ isLoading: true });
        const categories = await getCachedQuery<IBlogCategory>(
          {
            content_type: "blogCategory",
            limit: 100,
            order: "fields.title",
          },
          language
        );

        if (categories) {
          const topPostsQuery = getCachedQuery<IArticle>(
            {
              content_type: "article",
              "fields.title[exists]": true,
              limit: 4,
              include: 1,
              order: "-fields.date",
              select: "fields.title, fields.slug, fields.media",
            },
            language
          ).then((result) => ({
            category: {
              sys: { id: TOP_POSTS_ID, locale: language },
              fields: { title: this.props.t("topPosts"), slug: "" },
            } as unknown as Entry<IBlogCategory>,
            articles: result?.items ?? [],
          }));

          const perCategoryQueries = categories.items.map((category) =>
            getCachedQuery<IArticle>(
              {
                content_type: "article",
                "fields.blogCategory.sys.id": category.sys.id,
                "fields.title[exists]": true,
                limit: 4,
                include: 1,
                order: "-fields.date",
                select: "fields.title, fields.slug, fields.media",
              },
              language
            ).then((result) => ({
              category,
              articles: result?.items ?? [],
            }))
          );

          const [topPostsGroup, ...categoryGroups] = await Promise.all([
            topPostsQuery,
            ...perCategoryQueries,
          ]);

          const blogData = [
            ...(topPostsGroup.articles.length > 0 ? [topPostsGroup] : []),
            ...categoryGroups.filter((group) => group.articles.length > 0),
          ];

          this.setState({
            blogData,
            activeCategoryId: blogData[0]?.category.sys.id ?? null,
            isLoading: false,
          });
        }
        return;
      }

      if (this.props.links) {
        const data = await getCachedQuery<
          IInternalNavigationLink | IExternalNavigationLink
        >(this.getContentfulQuery(), language);
        if (data) {
          const linksData0: Entry<
            IInternalNavigationLink | IExternalNavigationLink
          >[] = [];
          // eslint-disable-next-line
          this.props.links.map((item, i) => {
            // eslint-disable-next-line
            data.items.map((item1, i1) => {
              if (item1.sys.id === item.sys.id) {
                linksData0.push(item1);
              }
            });
          });
          this.setState({ linksData: linksData0 });
        }
      } else {
        const data = await getCachedQuery<ICategory | IQuestionCategory>(
          this.getContentfulQuery(),
          language
        );
        if (data) {
          const dataItems = data.items
            .filter((item, i) => {
              if (
                // filter out categories with showSubmenu false
                !("showSubmenu" in item.fields) ||
                item.fields.showSubmenu === undefined ||
                item.fields.showSubmenu === true
              ) {
                return true;
              } else return false;
            })
            .filter((item, i) => {
              return (
                // filter out question categories we don't want to show in this language
                !("showInLanguage" in item.fields) ||
                !item.fields.showInLanguage ||
                item.fields.showInLanguage.includes(
                  // @ts-ignore
                  codeToLang[this.props.i18n.language]
                )
              );
            });
          this.setState({ data: dataItems });
        }
      }
    } finally {
      this.isFetching = false;
    }
  };

  public getContentfulQuery = () => {
    let contentfulQuery = {};
    switch (this.props.type) {
      case "Activity categories":
        contentfulQuery = {
          content_type: "category",
          "fields.type[match]": "Activity category",
          limit: 40,
          include: 2,
          order: "fields.name",
        };
        break;
      case "Accommodation categories":
        contentfulQuery = {
          content_type: "category",
          "fields.type[match]": "Accommodation category",
          limit: 20,
          include: 2,
          order: "fields.name",
        };
        break;
      case "Itineraries":
        contentfulQuery = {
          content_type: "itinerary",
          limit: 20,
          include: 0,
        };
        break;
      case "Question categories":
        contentfulQuery = {
          content_type: "questionCategory",
          limit: 40,
          include: 2,
          order: "fields.sortOrder",
        };
        break;
      case "Blogs":
        contentfulQuery = {
          content_type: "blogCategory",
          limit: 100,
          order: "fields.title",
        };
        break;
      case "Custom links":
        if (this.props.links) {
          const customLinks = this.props.links.map((item, i) => {
            return item.sys.id;
          });
          contentfulQuery = {
            "sys.id[in]": customLinks.join(","),
            limit: 40,
            include: 1,
          };
        }
        break;
      default:
        return {};
    }
    return contentfulQuery;
  };

  public render() {
    const { type, baseLink, baseImage, links, active = false } = this.props;
    const data = this.state.data;
    const linksData = this.state.linksData;
    const blogData = this.state.blogData;

    if (type === "Blogs") {
      if (this.state.isLoading) {
        return (
          <div
            aria-label="main navigation sub-menu"
            className={`nav__sub-menu ${active ? "--active" : ""}`}
          >
            <div className="nav__sub-menu-container nav__sub-menu-blog-loader">
              <img
                src={loadingSpinner}
                alt="Loading"
                className="nav__sub-menu-blog-spinner"
                height="50px"
                width="50px"
              />
            </div>
          </div>
        );
      }
      if (!blogData || blogData.length === 0) {
        return null;
      }
      const activeCategoryId =
        this.state.activeCategoryId ?? blogData[0].category.sys.id;
      const activeGroup = blogData.find(
        (g) => g.category.sys.id === activeCategoryId
      );
      return (
        <div
          aria-label="main navigation sub-menu"
          className={`nav__sub-menu ${
            active || this.isPrerender ? "--active" : ""
          }`}
        >
          <div className="nav__sub-menu-container nav__sub-menu-container--blogs">
            <ul className="nav__sub-menu-blog-categories -hide-mobile">
              {blogData.map((group) => (
                <li
                  key={group.category.sys.id}
                  className={`nav__sub-menu-blog-category${
                    activeCategoryId === group.category.sys.id
                      ? " --active"
                      : ""
                  }`}
                  onClick={() =>
                    this.setState({ activeCategoryId: group.category.sys.id })
                  }
                >
                  {group.category.fields.title}
                </li>
              ))}
            </ul>
            <ul className="nav__sub-menu-blog-categories -hide-desktop">
              {blogData.map((group) => (
                <li key={group.category.sys.id} className="nav__sub-item">
                  <LocaleNavigationLink
                    to={`${baseLink}?cats=${group.category.fields.slug}`}
                    baseLink={baseLink}
                    onClick={this.props.onClick}
                    className="submenu"
                  >
                    {group.category.fields.title}
                  </LocaleNavigationLink>
                </li>
              ))}
            </ul>
            <ul className="nav__sub-menu-blog-articles">
              {activeGroup?.articles.map((article) => (
                <li key={article.sys.id} className="nav__sub-menu-blog-article">
                  <LocaleNavigationLink
                    to={`/article/${article.fields.slug}`}
                    baseLink={baseLink}
                    onClick={this.props.onClick}
                    className="nav__sub-menu-blog-article-link"
                  >
                    {article.fields.media?.fields?.image && (
                      <PictureWrapper
                        options={[["fit", "fill"]]}
                        small={[200, 200]}
                        medium={[200, 200]}
                        large={[200, 200]}
                        asset={article.fields.media.fields.image}
                      />
                    )}
                    <span className="nav__sub-menu-blog-article-title">
                      {article.fields.title}
                    </span>
                  </LocaleNavigationLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    if ((!data || data.length === 0) && !links) {
      return null;
    }

    const columnSize =
      !data || (linksData.length < 10 && data.length < 6)
        ? 1
        : (linksData.length === 0 && data.length <= 100) ||
          (data.length === 0 && linksData.length <= 100)
        ? 2
        : 3;
    const contentType = (
      types.has(type) ? types.get(type) : "activityCategory"
    ) as CategorySystemName;

    return (
      <div
        aria-label="main navigation sub-menu"
        className={`
                    nav__sub-menu 
                    
										${links ? "-no-button" : ""}
                    ${active ? "--active" : ""}
                    ${!data ? "-no-content" : ""}
                    `}
      >
        <div className={`nav__sub-menu-container `}>
          {baseImage && (
            <PictureWrapper
              className={`nav__sub-menu-img`}
              options={[["fit", "fill"]]}
              small={[690, 480]}
              medium={[600, 415]}
              large={[690, 480]}
              asset={baseImage}
            />
          )}
          <ul className={`-column-${columnSize}`}>
            {!links &&
              data
                .sort(getCategorySort(contentType))
                .map(
                  (
                    subNavItem: Entry<ICategory | IQuestionCategory>,
                    key2: number
                  ) => (
                    <React.Fragment key={key2}>
                      <li className="nav__sub-item">
                        <LocaleNavigationLink
                          to={getCategoryUrl(
                            contentType,
                            subNavItem.fields.slug
                          )}
                          baseLink={baseLink}
                          onClick={this.props.onClick}
                          className="submenu"
                        >
                          {subNavItem.fields.name}
                        </LocaleNavigationLink>
                      </li>
                    </React.Fragment>
                  )
                )}
            {links &&
              linksData &&
              linksData.map(
                (
                  link: Entry<
                    IInternalNavigationLink | IExternalNavigationLink
                  >,
                  key2: number
                ) =>
                  link.sys.contentType.sys.id === "externalNavigationLink" ||
                  typeof link.fields.link === "string" ? (
                    <li className="nav__sub-item" key={key2}>
                      <NavigationLink
                        link={link as any}
                        onClick={this.props.onClick}
                      />
                    </li>
                  ) : (
                    <li className="nav__sub-item" key={key2}>
                      <LocaleNavigationLink
                        to={
                          link.fields.link.sys.contentType.sys.id === "article"
                            ? "/article/" + link.fields.link.fields.slug
                            : link.fields.link.sys.contentType.sys.id === "page"
                            ? link.fields.link.fields.slug
                            : "/"
                        }
                        baseLink={baseLink}
                        className="submenu"
                        onClick={this.props.onClick}
                      >
                        {link.fields.title}
                      </LocaleNavigationLink>
                    </li>
                  )
              )}
            {false && !links && (
              <MultiButton
                text={this.props.t("viewAll")}
                className="nav__sub-btn"
                to={baseLink}
                type="basic"
              />
            )}
          </ul>
        </div>
      </div>
    );
  }
}

export default withTranslation()(NavigationSubMenu);
